/**
 * PATH: packages/ui/src/components/settings/EntitiesTab.tsx
 * PURPOSE: Orchestrator for organisation entities and Xero tenant linking
 * WHY: CFO reporting and job attribution require correct entity <-> Xero mapping.
 * FLOW: Load Entities -> Link / Edit -> Save Changes
 * RELATED: entities-tab/EntityCard.tsx, entities-tab/AddEntityForm.tsx
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Plus, Link2, Loader2, RefreshCw, CheckCircle2, Network, Receipt, Building2 } from 'lucide-react'
import { EntityStructurePanel } from './entities-tab/EntityStructurePanel'
import { cn } from '../../lib/utils'
import { EntityCard } from './entities-tab/EntityCard'
import { AddEntityForm } from './entities-tab/AddEntityForm'
import { DEFAULT_FORM } from './entities-tab/entities-tab-types'
import type {
  OrganisationEntity,
  XeroTenant,
  EntityFormState,
  EntitiesTabProps,
} from './entities-tab/entities-tab-types'

// Re-export public types so external consumers don't need to change imports
export type { EntitiesTabProps } from './entities-tab/entities-tab-types'

export function EntitiesTab({
  apiClient,
  organisationId,
  isXeroConnected,
  xeroApplicable = true,
  onConnectXero,
  className,
}: EntitiesTabProps) {
  const [entities, setEntities] = useState<OrganisationEntity[]>([])
  const [tenants, setTenants] = useState<XeroTenant[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [selectedTenant, setSelectedTenant] = useState<Record<number, string>>({})
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingEntity, setEditingEntity] = useState<OrganisationEntity | null>(null)
  const [formState, setFormState] = useState<EntityFormState>(DEFAULT_FORM)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [confirmDefault, setConfirmDefault] = useState<{ entityId: number; entityName: string } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<OrganisationEntity | null>(null)
  const [viewMode, setViewMode] = useState<'xero' | 'structure'>('xero')

  const canUseApi = !!apiClient && !!organisationId
  // When Xero is not applicable to this org (enable_xero=false) we never
  // treat it as "connected" and never surface Xero tenant-mapping controls.
  const xeroConnected = xeroApplicable ? (isXeroConnected ?? tenants.length > 0) : false

  const fetchEntities = useCallback(async () => {
    if (!apiClient || !organisationId) return
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(`/organisations/${organisationId}/entities/`)
      const data = response?.data ?? response
      setEntities(Array.isArray(data?.entities) ? data.entities : [])
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status
      const serverMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      const errorMsg = err instanceof Error ? err.message :
        serverMsg || (status === 403 ? 'You do not have permission to access this organisation' :
        status === 401 ? 'Please log in again' :
        status === 404 ? 'Organisation not found' : 'Unable to load entities')
      setError(errorMsg)
    } finally {
      setLoading(false)
    }
  }, [apiClient, organisationId])

  const fetchTenants = useCallback(async () => {
    if (!apiClient || !organisationId) return
    try {
      const response = await apiClient.get('/xero/tenants/', { params: { tenant: organisationId } })
      const data = response?.data ?? response
      setTenants(Array.isArray(data?.tenants) ? data.tenants : [])
    } catch {
      setTenants([])
    }
  }, [apiClient, organisationId])

  useEffect(() => {
    fetchEntities()
    // Skip the Xero /tenants/ call entirely for non-Xero orgs — no point
    // hitting the integration, and it keeps the entity list provider-neutral.
    if (xeroApplicable) fetchTenants()
  }, [fetchEntities, fetchTenants, xeroApplicable])

  const openCreateDialog = () => {
    setEditingEntity(null)
    setFormState(DEFAULT_FORM)
    setDialogOpen(true)
  }

  const openEditDialog = (entity: OrganisationEntity) => {
    setEditingEntity(entity)
    setFormState({
      name: entity.name || '',
      legal_name: entity.legal_name || '',
      code: entity.code || '',
      abn: entity.abn || '',
      color: entity.color || '#6366f1',
    })
    setDialogOpen(true)
  }

  const handleSaveEntity = async () => {
    if (!apiClient || !organisationId) return
    if (!formState.name.trim()) { setDialogError('Entity name is required'); return }

    const abnDigits = formState.abn?.replace(/[\s-]/g, '')
    if (abnDigits && abnDigits.length !== 11) { setDialogError('ABN must be exactly 11 digits'); return }
    if (abnDigits && abnDigits[0] === '0') { setDialogError('ABN cannot start with 0'); return }

    setSaving(true)
    setDialogError(null)
    try {
      const payload = { ...formState, abn: abnDigits || '' }
      if (editingEntity) {
        await apiClient.patch?.(`/organisations/${organisationId}/entities/${editingEntity.id}/`, payload)
        setSuccessMsg(`"${formState.name}" updated successfully`)
      } else {
        await apiClient.post(`/organisations/${organisationId}/entities/`, payload)
        setSuccessMsg(`"${formState.name}" created successfully`)
      }
      setDialogOpen(false)
      await fetchEntities()
      setTimeout(() => setSuccessMsg(null), 4000)
    } catch (err: unknown) {
      const serverErr = (err as { response?: { data?: { error?: string; abn?: string[] } } })?.response?.data
      const msg = serverErr?.error || serverErr?.abn?.[0] || 'Unable to save entity'
      setDialogError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleRequestSetDefault = (entityId: number) => {
    const target = entities.find((e) => e.id === entityId)
    if (!target) return
    setConfirmDefault({ entityId, entityName: target.name })
  }

  const handleConfirmSetDefault = async () => {
    if (!apiClient || !organisationId || !confirmDefault) return
    setSaving(true)
    setError(null)
    try {
      await apiClient.post(`/organisations/${organisationId}/entities/${confirmDefault.entityId}/set-default/`, {})
      setSuccessMsg(`"${confirmDefault.entityName}" is now the default entity`)
      setConfirmDefault(null)
      await fetchEntities()
      setTimeout(() => setSuccessMsg(null), 4000)
    } catch {
      setError('Unable to set default entity')
      setConfirmDefault(null)
    } finally {
      setSaving(false)
    }
  }

  const handleRequestDelete = (entity: OrganisationEntity) => {
    setConfirmDelete(entity)
  }

  const handleConfirmDelete = async () => {
    if (!apiClient || !organisationId || !confirmDelete) return
    setSaving(true)
    setError(null)
    try {
      await apiClient.delete?.(`/organisations/${organisationId}/entities/${confirmDelete.id}/`)
      setSuccessMsg(`"${confirmDelete.name}" has been deleted`)
      setConfirmDelete(null)
      await fetchEntities()
      setTimeout(() => setSuccessMsg(null), 4000)
    } catch (err: unknown) {
      const serverErr = (err as { response?: { data?: { error?: string } } })?.response?.data
      setError(serverErr?.error || 'Unable to delete entity')
      setConfirmDelete(null)
    } finally {
      setSaving(false)
    }
  }

  const handleLinkTenant = async (entityId: number, tenantId: string) => {
    if (!apiClient || !organisationId) return
    const tenant = tenants.find((t) => t.id === tenantId)
    if (!tenant) return
    setSaving(true)
    setError(null)
    try {
      await apiClient.post(`/organisations/${organisationId}/entities/${entityId}/xero/`, {
        tenant_id: tenant.id,
        tenant_name: tenant.name,
      })
      await fetchEntities()
    } catch {
      setError('Unable to link Xero tenant')
    } finally {
      setSaving(false)
    }
  }

  const handleUnlinkTenant = async (entityId: number) => {
    if (!apiClient || !organisationId) return
    setSaving(true)
    setError(null)
    try {
      await apiClient.post(`/organisations/${organisationId}/entities/${entityId}/xero/`, { tenant_id: '' })
      await fetchEntities()
    } catch {
      setError('Unable to unlink Xero tenant')
    } finally {
      setSaving(false)
    }
  }

  const activeEntities = useMemo(() => entities.filter((e) => e.is_active !== false), [entities])

  if (!canUseApi) {
    return (
      <div className={cn('space-y-4 max-w-2xl', className)}>
        <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Connect an organisation to manage entities and Xero mappings.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Entities</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {viewMode === 'xero'
              ? (xeroApplicable
                  ? 'Link legal entities to distinct Xero tenants for accurate reporting.'
                  : 'Manage your organisation\u2019s legal entities.')
              : 'Define corporate group hierarchy and ultimate shareholders.'}
          </p>
          <div className="mt-3 inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('xero')}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                viewMode === 'xero'
                  ? 'bg-teal-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800',
              )}
            >
              {xeroApplicable ? <Receipt className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
              {xeroApplicable ? 'Xero mapping' : 'Entities'}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('structure')}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                viewMode === 'structure'
                  ? 'bg-teal-600 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800',
              )}
            >
              <Network className="w-3.5 h-3.5" />
              Corporate structure
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={fetchEntities}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
          {viewMode === 'xero' && (
            <button
              onClick={openCreateDialog}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" />
              Add Entity
            </button>
          )}
        </div>
      </div>

      {successMsg && (
        <div className="fixed top-4 right-4 z-[9999] p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/90 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2 shadow-lg max-w-sm animate-in slide-in-from-top-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          {successMsg}
        </div>
      )}

      {error && (
        <div className="fixed top-4 right-4 z-[9999] p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/90 text-sm text-red-600 dark:text-red-400 shadow-lg max-w-sm animate-in slide-in-from-top-2">
          {error}
        </div>
      )}

      {viewMode === 'structure' && organisationId && apiClient && (
        <EntityStructurePanel
          apiClient={apiClient}
          organisationId={organisationId}
          entities={activeEntities}
          onRefreshEntities={fetchEntities}
        />
      )}

      {viewMode === 'xero' && (
        <>
      {/* Xero Connection CTA — only when Xero is applicable to this org */}
      {xeroApplicable && !xeroConnected && !loading && (
        <div className="p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-800/30">
              <Link2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-amber-800 dark:text-amber-200">Connect Xero to map tenants</h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Once Xero is connected, you can map each entity to a Xero tenant for accurate financial reporting.
              </p>
              {onConnectXero && (
                <button
                  onClick={onConnectXero}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
                >
                  <Link2 className="w-4 h-4" />
                  Connect Xero in Integrations
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading entities...
        </div>
      )}

      {/* Entity list */}
      <div className="grid gap-4">
        {activeEntities.map((entity) => (
          <EntityCard
            key={entity.id}
            entity={entity}
            tenants={tenants}
            xeroApplicable={xeroApplicable}
            selectedTenantId={selectedTenant[entity.id] || ''}
            saving={saving}
            onSelectTenant={(tid) => setSelectedTenant((prev) => ({ ...prev, [entity.id]: tid }))}
            onSetDefault={() => handleRequestSetDefault(entity.id)}
            onEdit={() => openEditDialog(entity)}
            onDelete={() => handleRequestDelete(entity)}
            onLink={() => handleLinkTenant(entity.id, selectedTenant[entity.id])}
            onUnlink={() => handleUnlinkTenant(entity.id)}
          />
        ))}

        {!loading && activeEntities.length === 0 && !error && (
          <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 text-sm text-slate-500">
            {xeroApplicable
              ? 'No entities found. Create your first entity to start linking Xero tenants.'
              : 'No entities found. Create your first legal entity to get started.'}
          </div>
        )}
        {!loading && activeEntities.length === 0 && error && (
          <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 text-sm text-slate-500">
            Click &quot;+ Add Entity&quot; above to create your first entity.
          </div>
        )}
      </div>
        </>
      )}

      {/* Create / Edit dialog */}
      <AddEntityForm
        open={dialogOpen}
        editingEntity={editingEntity}
        formState={formState}
        saving={saving}
        error={dialogError}
        onFormChange={(updates) => setFormState((prev) => ({ ...prev, ...updates }))}
        onClose={() => { setDialogOpen(false); setDialogError(null) }}
        onSave={handleSaveEntity}
      />

      {/* Set Default confirmation dialog */}
      {confirmDefault && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDefault(null)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 p-6 z-[60]">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">Change Default Entity</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
              {(() => {
                const current = entities.find((e) => e.is_default)
                if (current) {
                  return <>
                    <span className="font-medium text-slate-900 dark:text-white">{current.name}</span> will no longer be the default.
                  </>
                }
                return 'No entity is currently set as default.'
              })()}
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
              <span className="font-medium text-slate-900 dark:text-white">{confirmDefault.entityName}</span> will become the new default for new jobs and quotes.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDefault(null)}
                className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSetDefault}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(null)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 p-6 z-[60]">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">Delete Entity</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
              Are you sure you want to delete <span className="font-medium text-slate-900 dark:text-white">{confirmDelete.name}</span>?
            </p>
            {(confirmDelete.job_count ?? 0) > 0 && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                This entity has {confirmDelete.job_count} job(s) assigned. They will be unlinked from this entity.
              </p>
            )}
            <p className="text-xs text-slate-500 mt-3">This action can be undone by an administrator.</p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EntitiesTab
